import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, Badge, SectionTitle } from "@/components/ui-hud";
import { Building2, Plane, ShieldCheck, MapPin, ArrowRight, Loader2, Users2, Ruler, Radio } from "lucide-react";

export default function OperatorDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get(`/operators/${id}`).then((r) => {
      setData(r.data);
      if (r.data.fleet?.length) setSelected(r.data.fleet[0]);
    }).catch(() => setData({ error: true }));
  }, [id]);

  if (!data) return <div className="max-w-5xl mx-auto px-6 py-16"><Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading…</div>;
  if (data.error) return <div className="max-w-5xl mx-auto px-6 py-16 text-white/60">Operator not found.</div>;

  const op = data.operator;
  const rp = op.role_profile || {};
  const fleet = data.fleet || [];

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        {rp.hero_photo_file_id ? (
          <div className="h-72 md:h-96 overflow-hidden">
            <img src={`/api/files/${rp.hero_photo_file_id}`} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/40 to-transparent" />
          </div>
        ) : (
          <div className="h-64 bg-gradient-to-br from-cyan/[0.10] to-transparent" />
        )}
        <div className="max-w-6xl mx-auto px-6 -mt-24 md:-mt-32 relative">
          <div className="flex items-end gap-5">
            {rp.company_logo_file_id ? (
              <img src={`/api/files/${rp.company_logo_file_id}`} alt="" className="h-24 w-24 rounded-xl border-2 border-obsidian object-contain bg-white/5" />
            ) : (
              <div className="h-24 w-24 rounded-xl border-2 border-obsidian bg-cyan/10 flex items-center justify-center"><Building2 className="h-10 w-10 text-cyan" /></div>
            )}
            <div className="pb-2 flex-1">
              <div className="overline text-cyan mb-1">// CHARTER OPERATOR</div>
              <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tighter leading-none">{rp.company_name || "Charter operator"}</h1>
              <div className="mt-2 flex flex-wrap gap-2 items-center">
                <span className="mono text-[11px] text-white/60">Part 135 · {rp.part_135_certificate || "—"}</span>
                {rp.d085_verified && <span className="mono text-[10px] px-2 py-0.5 rounded-full border border-avail/50 text-avail bg-avail/10 inline-flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> D085 VERIFIED</span>}
                {rp.argus_wyvern_rating && rp.argus_wyvern_rating !== "None" && (
                  <span className="mono text-[10px] px-2 py-0.5 rounded-full border border-orange-hud/40 text-orange-hud bg-orange-hud/10">{rp.argus_wyvern_rating}</span>
                )}
              </div>
            </div>
            <Link
              to={`/charter-request?operator_id=${encodeURIComponent(op.id)}&company=${encodeURIComponent(rp.company_name || "")}`}
              data-testid="op-request-cta"
              className="btn btn-primary"
              style={{background:"#4B9CFF", borderColor:"#4B9CFF"}}
            >REQUEST QUOTE <ArrowRight className="h-3.5 w-3.5" /></Link>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-[1fr_320px] gap-8">
        {/* Fleet Gallery */}
        <div>
          <div className="overline text-cyan mb-3">// FLEET · {fleet.length} AIRCRAFT</div>
          {fleet.length === 0 && <Card><div className="text-center py-8 text-white/50">No aircraft on file yet.</div></Card>}
          {fleet.length > 0 && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-5">
                {fleet.map((ac) => (
                  <button key={ac.id} onClick={() => setSelected(ac)} className={`text-left rounded-lg overflow-hidden border transition-all ${selected?.id === ac.id ? "border-cyan" : "border-white/10 hover:border-white/30"}`}>
                    {ac.exterior_photos?.[0] ? (
                      <img src={`/api/files/${ac.exterior_photos[0]}`} className="w-full aspect-video object-cover" alt="" />
                    ) : (
                      <div className="w-full aspect-video bg-white/[0.03] flex items-center justify-center text-white/30"><Plane className="h-6 w-6" /></div>
                    )}
                    <div className="p-2">
                      <div className="mono text-xs font-bold text-cyan">{ac.tail_number}</div>
                      <div className="text-[10px] mono text-white/50">{ac.type_code}{ac.year ? ` · ${ac.year}` : ""}</div>
                    </div>
                  </button>
                ))}
              </div>

              {selected && (
                <Card testId={`ac-detail-${selected.tail_number}`}>
                  <div className="flex items-baseline justify-between gap-3 mb-3">
                    <div>
                      <div className="mono text-2xl font-bold text-cyan">{selected.tail_number}</div>
                      <div className="mono text-xs text-white/60">{selected.type_code}{selected.year ? ` · ${selected.year}` : ""}{selected.home_base_icao ? ` · Home ${selected.home_base_icao}` : ""}</div>
                    </div>
                    {selected.charter_ready ? (
                      <Badge tone="cyan" pulse>CHARTER READY</Badge>
                    ) : (
                      <Badge tone="mono">UNAVAILABLE</Badge>
                    )}
                  </div>

                  {/* Per-aircraft charter CTA — deep-links to /charter-request with this tail locked. */}
                  {selected.charter_ready && (
                    <div className="mb-4 rounded-lg border border-cyan/25 bg-cyan/[0.04] p-3 flex items-center gap-3 flex-wrap" data-testid={`ac-charter-cta-${selected.tail_number}`}>
                      <div className="flex-1 min-w-[220px]">
                        <div className="mono text-[10px] tracking-widest text-cyan">// CHARTER THIS AIRCRAFT</div>
                        <div className="text-sm text-white/85 mt-0.5">Send a quote request directly to {op.role_profile?.company_name || "the operator"} for <span className="mono text-cyan">{selected.tail_number}</span>.</div>
                      </div>
                      <Link
                        to={`/charter-request?operator_id=${encodeURIComponent(op.id)}&aircraft_id=${encodeURIComponent(selected.id)}&tail=${encodeURIComponent(selected.tail_number)}&type=${encodeURIComponent(selected.type_code || "")}&company=${encodeURIComponent(op.role_profile?.company_name || "")}`}
                        data-testid={`ac-request-quote-${selected.tail_number}`}
                        className="btn btn-primary"
                      >
                        REQUEST QUOTE · CHECK AVAILABILITY <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                  )}

                  <div className="grid grid-cols-3 gap-3 py-3 border-y border-white/[0.06]">
                    <StatRow icon={<Users2 className="h-3.5 w-3.5" />} label="Seats" v={selected.seats || "—"} />
                    <StatRow icon={<Ruler className="h-3.5 w-3.5" />} label="Range" v={selected.range_nm ? `${selected.range_nm} NM` : "—"} />
                    <StatRow icon={<Radio className="h-3.5 w-3.5" />} label="Live" v={<TrackerHint icao24={selected.adsb_hex_code} />} />
                  </div>

                  {selected.hourly_rate_usd ? (
                    <div className="mt-3 flex items-center gap-2 text-[11px] mono text-white/60">
                      <span className="text-white/45">HOURLY BILLABLE RATE</span>
                      <span className="text-avail text-sm">${Number(selected.hourly_rate_usd).toLocaleString()} / hr</span>
                    </div>
                  ) : null}

                  {selected.exterior_photos?.length > 0 && (
                    <PhotoRow title="EXTERIOR" ids={selected.exterior_photos} />
                  )}
                  {selected.interior_photos?.length > 0 && (
                    <PhotoRow title="INTERIOR" ids={selected.interior_photos} />
                  )}
                  {selected.videos?.length > 0 && (
                    <div className="mt-4">
                      <div className="overline mb-2">// VIDEOS</div>
                      <div className="grid grid-cols-2 gap-2">
                        {selected.videos.map((vid, i) => (
                          <video key={i} src={`/api/files/${vid}`} controls className="w-full rounded-md" />
                        ))}
                      </div>
                    </div>
                  )}

                  {selected.notes && (
                    <div className="mt-4 p-3 rounded-md bg-black/40 border border-white/[0.06] text-sm text-white/70 whitespace-pre-wrap">{selected.notes}</div>
                  )}
                </Card>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <aside className="space-y-4">
          <Card>
            <div className="overline text-cyan mb-3">// COVERAGE</div>
            <SidebarRow icon={<MapPin className="h-3.5 w-3.5" />} label="Primary Bases" v={(rp.primary_bases || []).join(", ") || "—"} mono />
            <SidebarRow label="Service Regions" v={(rp.service_regions || []).join(", ") || "—"} />
            <SidebarRow label="Hiring" v={normalizeHiring(rp.hiring_status)} />
            <SidebarRow label="Safety Rating" v={rp.argus_wyvern_rating || "—"} />
          </Card>
        </aside>
      </section>
    </div>
  );
}

function StatRow({ icon, label, v }) {
  return (
    <div>
      <div className="eyebrow-muted flex items-center gap-1 mb-0.5">{icon} {label}</div>
      <div className="mono text-cyan text-sm">{v}</div>
    </div>
  );
}

function TrackerHint({ icao24 }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!icao24 || icao24.length !== 6) { setState({ online: false }); return; }
    api.get(`/adsb/lookup/${icao24}`)
      .then((r) => setState({ online: !!r.data?.live, ...(r.data?.live || {}) }))
      .catch(() => setState({ online: false }));
  }, [icao24]);
  if (!state) return <Loader2 className="h-3 w-3 animate-spin text-cyan" />;
  if (!state.online) return <span className="text-white/40 mono text-[10px]">not in air</span>;
  const alt = state.alt_baro;
  return <span className="text-avail mono text-[11px]">{alt != null ? `${Math.round(alt).toLocaleString()} ft` : "airborne"}</span>;
}

function PhotoRow({ title, ids }) {
  return (
    <div className="mt-4">
      <div className="overline mb-2">// {title}</div>
      <div className="grid grid-cols-3 gap-2">
        {ids.map((fid, i) => (
          <a key={i} href={`/api/files/${fid}`} target="_blank" rel="noreferrer" className="aspect-video rounded overflow-hidden border border-white/10">
            <img src={`/api/files/${fid}`} alt="" className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
    </div>
  );
}

function SidebarRow({ icon, label, v, mono }) {
  return (
    <div className="py-2 border-b border-white/[0.05] last:border-b-0">
      <div className="eyebrow-muted flex items-center gap-1 mb-1">{icon} {label}</div>
      <div className={`text-sm text-white/85 ${mono ? "mono" : ""}`}>{v}</div>
    </div>
  );
}

// Map any legacy hiring_status value to the current two-option set so old operator
// profiles don't display stale copy ("Actively hiring" etc.).
function normalizeHiring(v) {
  if (!v) return "—";
  const s = String(v).toLowerCase();
  if (s.includes("not") || s.includes("selective")) return "Not available";
  return "Available for charter requests";
}
